import React, { useEffect, useState, useCallback } from "react";
import "./LeadDetails.css";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../../config/env";

interface TimelineItem {
  id: string;
  type: string;
  agent_name: string | null;
  role_id?: number | null;
  timeline_time: string;
  otherDetails: any;
  profile_image: string | null;
  time_diff: string;
}

interface RawLeadData {
  id: number;
  name: string;
  email: string | null;
  number: string;
  remarks: string | null;
  voice_note: string | null;
  recording_link: string | null;
  call_duration?: string;
  customer_address: string | null;
  buying_location: string | null;
  property_type: string | null;
  configuration: string | null;
  budget_min: string | null;
  budget_max: string | null;
  created_at: string;
  city?: string;
  [key: string]: any;
}

interface LeadData {
  rawLead: RawLeadData;
  timeline: TimelineItem[];
  leadId: string | null;
  inquiriesCount: number;
}

const RawLeadDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [leadData, setLeadData] = useState<LeadData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"updated" | "initial">("updated");
  const location = useLocation();

  const {
    raw_lead_id,
    raw_leads_remarks,
    customer_address,
    raw_leads_buying_location,
    raw_leads_property_type,
    raw_leads_configuration,
    raw_leads_budget_max,
    raw_leads_budget_min,
    bhk,
    city,
    voice_note,
  } = location.state || {};

  const calculateTimeDiff = useCallback((timelineTime: string): string => {
    try {
      const now = new Date();
      const past = new Date(timelineTime);
      const diffMs = now.getTime() - past.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return `${Math.floor(diffDays / 7)}w ago`;
    } catch (error) {
      return "Recently";
    }
  }, []);

  const getRoleDisplayName = useCallback((roleId?: number | null): string => {
    switch (roleId) {
      case 4:
        return "Sales Agent";
      case 5:
        return "Presales Agent";
      default:
        return "System Generated";
    }
  }, []);

  useEffect(() => {
    const fetchLeadDetails = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          alert("Session expired, please login again");
          navigate("/");
          return;
        }

        const res = await axios.get(
          `${API_BASE_URL}/api/global-partner-connect/raw-leads-history/${raw_lead_id || id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );

        const parsedData = {
          ...res.data.data,
          timeline: res.data.data.timeline.map((item: TimelineItem) => {
            let parsedDetails = {};
            try {
              parsedDetails = typeof item.otherDetails === 'string'
                ? JSON.parse(item.otherDetails)
                : item.otherDetails || {};
            } catch (error) {
              console.error("Error parsing otherDetails:", error);
              parsedDetails = {};
            }

            return {
              ...item,
              otherDetails: parsedDetails,
              time_diff: calculateTimeDiff(item.timeline_time)
            };
          })
        };

        setLeadData(parsedData);
      } catch (error: any) {
        console.error("Error fetching lead details:", error);

        if (error?.response?.status === 401) {
          localStorage.removeItem("authToken");
          alert("Session expired, please login again");
          navigate("/");
        } else {
          alert("Cannot fetch lead details");
        }
      } finally {
        setLoading(false);
      }
    };

    if (raw_lead_id || id) {
      fetchLeadDetails();
    }
  }, [raw_lead_id, id, navigate, calculateTimeDiff]);

  const handleImageClick = useCallback((imageUrl: string | undefined) => {
    if (imageUrl) {
      setSelectedImage(imageUrl);
    }
  }, []);

  if (loading) return <p className="loadingText">Loading lead details...</p>;
  if (!leadData) return <p className="loadingText">No lead data found</p>;

  const firstTimelineItem = leadData.timeline[0];
  const clientName = firstTimelineItem?.otherDetails?.client_name || leadData.rawLead?.name || "Unknown Client";

  const getRecordingLink = (otherDetails: any): string | null => {
    if (!otherDetails) return null;
    return otherDetails.recording || otherDetails.recording_link || null;
  };

  const formatTimelineItem = (item: TimelineItem) => {
    const { type, timeline_time, otherDetails, time_diff, role_id, agent_name } = item;
    const roleDisplayName = getRoleDisplayName(role_id);
    const recordingLink = getRecordingLink(otherDetails);
    const hasRecording = !!recordingLink;

    switch (type) {
      case "leadgen":
        return {
          icon: "📥",
          title: "Lead Generated",
          description: `Source: ${otherDetails.source_of_lead || "Unknown"}`,
          time: timeline_time,
          role: roleDisplayName,
          timeDiff: time_diff,
          hasRecording,
          recordingLink,
          agentName: agent_name || otherDetails.agent_name
        };
      case "system_call":
        return {
          icon: "📞",
          title: "Call Attempted",
          description: otherDetails.cust_status || "System call was made to client",
          time: timeline_time,
          role: roleDisplayName,
          timeDiff: time_diff,
          hasRecording,
          recordingLink,
          agentName: agent_name
        };
      case "sales_recording_call":
      case "recording":
        return {
          icon: "🎙️",
          title: type === "sales_recording_call" ? "Sales Call" : "Recording Added",
          description: otherDetails.remark || `Call recording available`,
          time: timeline_time,
          role: roleDisplayName,
          timeDiff: time_diff,
          hasRecording,
          recordingLink,
          agentName: agent_name
        };
      case "lead":
        return {
          icon: "📋",
          title: "Lead Details Updated",
          description: otherDetails.requirement || "Lead requirements updated",
          time: timeline_time,
          role: roleDisplayName,
          timeDiff: time_diff,
          hasRecording,
          recordingLink,
          agentName: agent_name
        };
      case "meeting":
        return {
          icon: "📅",
          title: "Meeting Scheduled",
          description: `Meeting at ${otherDetails.address || "Unknown location"} on ${otherDetails.meeting_date || ""} ${otherDetails.meeting_time || ""}`,
          time: timeline_time,
          role: roleDisplayName,
          timeDiff: time_diff,
          hasRecording,
          recordingLink,
          agentName: agent_name
        };
      case "disposition":
        return {
          icon: "🏷️",
          title: otherDetails.despositions_name || "Disposition Updated",
          description: otherDetails.remark || "Status updated",
          time: timeline_time,
          role: roleDisplayName,
          timeDiff: time_diff,
          hasRecording,
          recordingLink,
          agentName: agent_name
        };
      default:
        return {
          icon: "🔹",
          title: type || "Activity",
          description: "Activity performed",
          time: timeline_time,
          role: roleDisplayName,
          timeDiff: time_diff,
          hasRecording,
          recordingLink,
          agentName: agent_name
        };
    }
  };

  // Get latest disposition for status
  const latestDisposition = [...leadData.timeline]
    .reverse()
    .find(item => item.type === "disposition");
  const currentStatus = latestDisposition?.otherDetails?.despositions_name || "New Lead";

  // Prepare data for tabs
  const updatedDetails = {
    lead_id: leadData.rawLead?.id || raw_lead_id || id || "N/A",
    city: leadData.rawLead?.city || city || "N/A",
    buying_location: leadData.rawLead?.buying_location || raw_leads_buying_location || "N/A",
    budget_range: (leadData.rawLead?.budget_min || raw_leads_budget_min) && (leadData.rawLead?.budget_max || raw_leads_budget_max)
      ? `₹${leadData.rawLead?.budget_min || raw_leads_budget_min} Lakhs - ₹${leadData.rawLead?.budget_max || raw_leads_budget_max} Crores`
      : "N/A",
    bhk: leadData.rawLead?.configuration || raw_leads_configuration || bhk || "N/A",
    property_type: leadData.rawLead?.property_type || raw_leads_property_type || "N/A",
    customer_address: leadData.rawLead?.customer_address || customer_address || "N/A",
    remarks: leadData.rawLead?.remarks || raw_leads_remarks || "N/A"
  };

  const initialDetails = {
    lead_id: leadData.rawLead?.id || "N/A",
    city: leadData.rawLead?.city || "N/A",
    buying_location: leadData.rawLead?.buying_location || "N/A",
    budget_range: leadData.rawLead?.budget_min && leadData.rawLead?.budget_max
      ? `₹${leadData.rawLead?.budget_min} Lakhs - ₹${leadData.rawLead?.budget_max} Crores`
      : "N/A",
    bhk: leadData.rawLead?.configuration || "N/A",
    property_type: leadData.rawLead?.property_type || "N/A",
    customer_address: leadData.rawLead?.customer_address || "N/A",
    remarks: leadData.rawLead?.remarks || "N/A"
  };

  const voiceNoteUrl = leadData.rawLead?.voice_note || voice_note || null;

  return (
    <div className="leadPage">
      {selectedImage && (
        <div className="imageModal" onClick={() => setSelectedImage(null)}>
          <div className="imageModalContent">
            <span className="closeModal" onClick={() => setSelectedImage(null)}>&times;</span>
            <img src={selectedImage} alt="Site Visit" className="modalImage" />
          </div>
        </div>
      )}

      <div className="leadDetailsContainer">
        <div className="leadHeader">
          <div className="headerTop" onClick={() => navigate("/dashboard")}>
            ← Back to Leads
          </div>

          <div className="leadProfile">
            <div className="avatar">👤</div>
            <div className="profileInfo">
              <h2>{clientName}</h2>
              <button className={`followBtn ${currentStatus.toLowerCase().replace(/\s+/g, '-')}`}>
                {currentStatus}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="contentContainer">
        {/* Tab Navigation */}
        <div className="tabsContainer">
          <button
            className={`tabBtn ${activeTab === "updated" ? "active" : ""}`}
            onClick={() => setActiveTab("updated")}
          >
            Updated Details
          </button>
          <button
            className={`tabBtn ${activeTab === "initial" ? "active" : ""}`}
            onClick={() => setActiveTab("initial")}
          >
            Initial Details
          </button>
        </div>

        {/* Updated Details Content */}
        {activeTab === "initial" && (
          <div className="cardBox">
            <h3>Client Initial Details</h3>
            <div className="detailsTable">
              <div className="tableRow">
                <div className="tableLabel">LEAD ID</div>
                <div className="tableValue">{updatedDetails.lead_id}</div>
              </div>
              <div className="tableRow">
                <div className="tableLabel">CITY</div>
                <div className="tableValue">{updatedDetails.city}</div>
              </div>
              <div className="tableRow">
                <div className="tableLabel">BUYING LOCATION</div>
                <div className="tableValue">{updatedDetails.buying_location}</div>
              </div>
              <div className="tableRow">
                <div className="tableLabel">BUDGET RANGE</div>
                <div className="tableValue">{updatedDetails.budget_range}</div>
              </div>
              <div className="tableRow">
                <div className="tableLabel">BHK</div>
                <div className="tableValue">{updatedDetails.bhk}</div>
              </div>
              <div className="tableRow">
                <div className="tableLabel">PROPERTY TYPE</div>
                <div className="tableValue">{updatedDetails.property_type}</div>
              </div>
              <div className="tableRow">
                <div className="tableLabel">CUSTOMER ADDRESS</div>
                <div className="tableValue">{updatedDetails.customer_address}</div>
              </div>
              <div className="tableRow">
                <div className="tableLabel">REMARKS</div>
                <div className="tableValue">{updatedDetails.remarks}</div>
              </div>
              {voiceNoteUrl && (
                <div className="tableRow">
                  <div className="tableLabel">VOICE NOTE</div>
                  <div className="tableValue">
                    <audio controls className="audioPlayer">
                      <source src={voiceNoteUrl} type="audio/mpeg" />
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Initial Details Content */}
        {activeTab === "updated" && (
          <div className="cardBox">
            <h3>Client Updated Details</h3>
            <div className="detailsTable">
              <div className="tableRow">
                <div className="tableLabel">LEAD ID</div>
                <div className="tableValue">{initialDetails.lead_id}</div>
              </div>
              <div className="tableRow">
                <div className="tableLabel">CITY</div>
                <div className="tableValue">{initialDetails.city}</div>
              </div>
              <div className="tableRow">
                <div className="tableLabel">BUYING LOCATION</div>
                <div className="tableValue">{initialDetails.buying_location}</div>
              </div>
              <div className="tableRow">
                <div className="tableLabel">BUDGET RANGE</div>
                <div className="tableValue">{initialDetails.budget_range}</div>
              </div>
              <div className="tableRow">
                <div className="tableLabel">BHK</div>
                <div className="tableValue">{initialDetails.bhk}</div>
              </div>
              <div className="tableRow">
                <div className="tableLabel">PROPERTY TYPE</div>
                <div className="tableValue">{initialDetails.property_type}</div>
              </div>
              <div className="tableRow">
                <div className="tableLabel">CUSTOMER ADDRESS</div>
                <div className="tableValue">{initialDetails.customer_address}</div>
              </div>
              <div className="tableRow">
                <div className="tableLabel">REMARKS</div>
                <div className="tableValue">{initialDetails.remarks}</div>
              </div>
            </div>
          </div>
        )}

        {/* Timeline Section */}
        <div className="cardBox">
          <h3>Timeline</h3>
          <div className="timelineContainer">
            {leadData.timeline.length === 0 ? (
              <div className="noTimeline">
                <p>No activity recorded yet.</p>
              </div>
            ) : (
              leadData.timeline.map((item, index) => {
                const formattedItem = formatTimelineItem(item);
                const isSiteVisit = item.type === "disposition" && item.otherDetails?.despositions_name === "Site Visit Done";
                const hasImage = !!item.otherDetails?.image;

                return (
                  <div key={item.id || index} className="timelineRow">
                    {index !== 0 && <div className="timelineLine"></div>}
                    <div className="timelineIcon">{formattedItem.icon}</div>
                    <div className="timelineContent">
                      <div className="timelineHeader">
                        <h4 className="timelineTitle">{formattedItem.title}</h4>
                        <span className="timelineTime">
                          {new Date(formattedItem.time).toLocaleDateString()} •{" "}
                          {new Date(formattedItem.time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                      <p className="timelineDescription">{formattedItem.description}</p>
                      {formattedItem.agentName && (
                        <p className="timelineAgent">👤 {formattedItem.role}</p>
                      )}

                      {/* Recording Player */}
                      {formattedItem.hasRecording && formattedItem.recordingLink && !isSiteVisit && (
                        <div className="recordingCard">
                          <div className="recordingHeader">
                            <span className="recordingIcon">🎙️</span>
                            <span className="recordingTitle">Call Recording</span>
                          </div>
                          <audio controls className="audioPlayer">
                            <source src={formattedItem.recordingLink} type="audio/mpeg" />
                            Your browser does not support the audio element.
                          </audio>
                        </div>
                      )}

                      {/* Site Visit Details */}
                      {isSiteVisit && (
                        <div className="siteVisitInlineCard">
                          <div className="siteVisitGrid">
                            <div className="infoSection">
                              <div className="detailRow">
                                <span className="detailLabel">Developer:</span>
                                <span className="detailValue">{item.otherDetails?.developer_name || "N/A"}</span>
                              </div>
                              <div className="detailRow">
                                <span className="detailLabel">Cluster:</span>
                                <span className="detailValue">{item.otherDetails?.cluster_name || "N/A"}</span>
                              </div>
                              <div className="detailRow">
                                <span className="detailLabel">Project Name:</span>
                                <span className="detailValue">{item.otherDetails?.project_name || "N/A"}</span>
                              </div>
                              <div className="detailRow">
                                <span className="detailLabel">Remarks:</span>
                                <span className="detailValue">{item.otherDetails?.remark || "N/A"}</span>
                              </div>
                              <div className="detailRow">
                                <span className="detailLabel">Disposition:</span>
                                <span className="detailValue highlight">{item.otherDetails?.despositions_name || "N/A"}</span>
                              </div>
                              <div className="detailRow">
                                <span className="detailLabel">Scheduled Date Time:</span>
                                <span className="detailValue">
                                  {item.otherDetails?.date && item.otherDetails?.time
                                    ? `${item.otherDetails.date} ${item.otherDetails.time}`
                                    : item.otherDetails?.meeting_date && item.otherDetails?.meeting_time
                                      ? `${item.otherDetails.meeting_date} ${item.otherDetails.meeting_time}`
                                      : "N/A"}
                                </span>
                              </div>
                              {formattedItem.recordingLink && (
                                <div className="recordingInline">
                                  <div className="recordingHeaderInline">
                                    <span className="recordingIconInline">🎙️</span>
                                    <span className="recordingTitleInline">Call Recording</span>
                                  </div>
                                  <audio controls className="audioPlayerInline">
                                    <source src={formattedItem.recordingLink} type="audio/mpeg" />
                                    Your browser does not support the audio element.
                                  </audio>
                                </div>
                              )}
                            </div>
                            {hasImage && (
                              <div className="imageSection">
                                <div className="imageWrapper">
                                  <img
                                    src={item.otherDetails.image}
                                    alt="Site Visit"
                                    className="siteVisitImageInline"
                                    onClick={() => handleImageClick(item.otherDetails.image)}
                                  />
                                  <div className="imageOverlayInline" onClick={() => handleImageClick(item.otherDetails.image)}>
                                    <span className="viewIconInline">🔍</span>
                                    <span>Click to view</span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="timelineFooter">
                        <span className="timelineTimeDiff">{formattedItem.timeDiff}</span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RawLeadDetails;